import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Satellite, Map as MapIcon, Navigation } from 'lucide-react';

const G_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.REACT_APP_GOOGLE_MAPS_KEY || '';

let _loaderPromise = null;
function _loadGmaps() {
  if (typeof window !== 'undefined' && window.google?.maps) return Promise.resolve(window.google);
  if (_loaderPromise) return _loaderPromise;
  _loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${G_KEY}&libraries=places,drawing&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google);
    s.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(s);
  });
  return _loaderPromise;
}

/**
 * Google Maps sharp-pin picker with satellite toggle.
 *
 * Props:
 *   value: {latitude, longitude} | null — initial pin
 *   onChange({latitude, longitude}) — called when the user moves / confirms the pin
 *   height: CSS height for the map container
 */
export default function GoogleMapPicker({ value, onChange, height = 320 }) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [mapType, setMapType] = useState('hybrid'); // 'hybrid' (satellite + labels) | 'roadmap'

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const google = await _loadGmaps();
        if (cancelled) return;

        const initLat = value?.latitude ?? 11.0573;
        const initLng = value?.longitude ?? 77.3401;

        const map = new google.maps.Map(mapEl.current, {
          center: { lat: initLat, lng: initLng },
          zoom: 18,
          mapTypeId: 'hybrid',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          tilt: 0,
        });
        mapRef.current = map;

        const marker = new google.maps.Marker({
          position: { lat: initLat, lng: initLng },
          map,
          draggable: true,
          animation: google.maps.Animation.DROP,
        });
        markerRef.current = marker;

        const emit = (ll) => onChange?.({ latitude: ll.lat(), longitude: ll.lng() });

        marker.addListener('dragend', (e) => emit(e.latLng));
        map.addListener('click', (e) => {
          marker.setPosition(e.latLng);
          emit(e.latLng);
        });

        // Fire an initial onChange so the parent knows the default location
        if (!value) emit(marker.getPosition());
        setLoading(false);
      } catch (e) {
        setErr(e.message || 'Failed to load map');
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !value) return;
    const pos = { lat: value.latitude, lng: value.longitude };
    if (markerRef.current) markerRef.current.setPosition(pos);
    map.panTo(pos);
  }, [value]);

  const toggleMapType = () => {
    const next = mapType === 'hybrid' ? 'roadmap' : 'hybrid';
    setMapType(next);
    mapRef.current?.setMapTypeId(next);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        mapRef.current?.panTo(pos);
        mapRef.current?.setZoom(19);
        markerRef.current?.setPosition(pos);
        onChange?.({ latitude: pos.lat, longitude: pos.lng });
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  if (!G_KEY) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
        Google Maps key missing. Set <code>REACT_APP_GOOGLE_MAPS_KEY</code> in frontend/.env.
      </div>
    );
  }

  return (
    <div className="relative w-full rounded-lg overflow-hidden border border-gray-200" data-testid="gmap-picker">
      <div ref={mapEl} style={{ width: '100%', height }} />
      {loading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center text-xs">Loading map…</div>}
      {err && <div className="absolute inset-0 bg-red-50 flex items-center justify-center text-xs text-red-700 px-4 text-center">{err}</div>}
      {!loading && !err && (
        <div className="absolute top-2 right-2 flex flex-col gap-1.5">
          <button
            type="button"
            onClick={toggleMapType}
            className="bg-white hover:bg-gray-100 px-2 py-1.5 rounded-md shadow text-[11px] font-semibold flex items-center gap-1"
            data-testid="gmap-toggle-satellite"
            title={mapType === 'hybrid' ? 'Switch to Map view' : 'Switch to Satellite view'}
          >
            {mapType === 'hybrid' ? <MapIcon size={11} /> : <Satellite size={11} />}
            {mapType === 'hybrid' ? 'Map' : 'Satellite'}
          </button>
          <button
            type="button"
            onClick={useMyLocation}
            className="bg-white hover:bg-gray-100 px-2 py-1.5 rounded-md shadow text-[11px] font-semibold flex items-center gap-1"
            data-testid="gmap-use-my-location"
            title="Use my current location"
          ><Navigation size={11} />My loc</button>
        </div>
      )}
      <div className="absolute bottom-2 left-2 bg-white/90 px-2 py-1 rounded text-[10px] text-gray-700 flex items-center gap-1 shadow">
        <MapPin size={11} className="text-red-500" />
        Tap the map or drag the pin to place it
      </div>
    </div>
  );
}
